import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, Phone, Bot } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useGetOwnPhoneNumbersQuery,
  useGetWorkflowsQuery,
  useGetAiAgentsQuery,
  useGetCallDispositionsQuery,
  useGetCallScriptsQuery,
  useGetContactListsQuery,
  useGetMembersQuery,
  useGetQuietHoursPolicyQuery,
  useGetTelephonySettingsQuery,
} from "@/store/rest_api";
import { QuietHoursPolicy } from "@/enum/QuietHoursPolicy";
import { CampaignAgentAccess } from "@/enum/CampaignAgentAccess";
import { DialerSeat } from "@/enum/DialerSeat";
import { MembershipStatus } from "@/enum/MembershipStatus";
import { DialerNoConsentMode } from "@/enum/DialerNoConsentMode";
import Campaign, { CampaignFormData, CampaignSettings } from "@/model/interface/Campaign";
import AudioClipPicker from "@/components/pages/campaigns/AudioClipPicker";
import { CampaignType } from "@/enum/CampaignType";
import { CampaignTargetType } from "@/enum/CampaignTargetType";
import { DialingMode } from "@/enum/DialingMode";
import { MachineDetectionMode } from "@/enum/MachineDetectionMode";
import { AddOnType } from "@/enum/AddOnType";
import { useAddOn } from "@/hooks/useAddOn";
import {
  DEFAULT_RETRY_MINUTES,
  MAX_AI_CALLS,
  MAX_DAILY_ATTEMPTS,
  MAX_LINES,
  MAX_PHONES_PER_LEAD,
  MAX_RETRY_ATTEMPTS,
  MIN_RING_SECONDS,
  formatRetryDelay,
  retryDelayChoices,
} from "@/util/dialer";
import { DialerNextPhoneTiming } from "@/enum/DialerNextPhoneTiming";
import { DialerQueueOrder } from "@/enum/DialerQueueOrder";
import { AudioLibrary } from "@/enum/AudioLibrary";
import { EMPTY_AUDIENCE_FILTER, parseAudienceFilter, serializeAudienceFilter } from "@/util/campaignAudience";
import { memberName } from "@/util/member";
import CampaignAudienceCard from "@/components/pages/campaigns/CampaignAudienceCard";
import StepIndicator from "@/components/pages/settings/telephony/a2p/StepIndicator";

interface CampaignFormProps {
  initialData?: Campaign;
  onSubmit: (data: CampaignFormData) => Promise<void>;
  isSubmitting: boolean;
  submitLabel: string;
  leadsContent?: React.ReactNode;
}

const DETAILS_STEP = 1;

const LEADS_STEP = 2;

const CAMPAIGN_STEPS = [
  { label: "Details", description: "How the campaign dials" },
  { label: "Leads", description: "Who the campaign calls" },
];

const NO_RETRY = "NO_RETRY";

const NO_DAILY_LIMIT = "NO_DAILY_LIMIT";

const NO_SCRIPT = "NO_SCRIPT";

const RETRY_OUTCOME_FIELDS: {
  name: "retryNoAnswerMinutes" | "retryBusyMinutes" | "retryVoicemailMinutes" | "retryFailedMinutes";
  label: string;
}[] = [
  { name: "retryNoAnswerMinutes", label: "No Answer" },
  { name: "retryBusyMinutes", label: "Busy" },
  { name: "retryVoicemailMinutes", label: "Voicemail" },
  { name: "retryFailedMinutes", label: "Failed Call" },
];

const QUEUE_ORDER_HELP: Record<DialerQueueOrder, string> = {
  [DialerQueueOrder.STANDARD]: "Callbacks that are due come first, then retries that are due, then new leads.",
  [DialerQueueOrder.CLOSING_WINDOW_FIRST]:
    "After callbacks, leads whose calling hours end soonest are called first so fewer run out of time.",
};

const DEFAULT_SETTINGS: CampaignSettings = {
  callsPerAgent: 3,
  retryAttempts: 0,
  retryDelayMinutes: 60,
  retryNoAnswerMinutes: DEFAULT_RETRY_MINUTES,
  retryBusyMinutes: DEFAULT_RETRY_MINUTES,
  retryVoicemailMinutes: DEFAULT_RETRY_MINUTES,
  retryFailedMinutes: null,
  maxAttemptsPerDay: null,
  queueOrder: DialerQueueOrder.STANDARD,
  ringTimeout: 30,
  wrapUpTime: 0,
  machineDetectionMode: MachineDetectionMode.CONNECT_FIRST,
  noConsentMode: DialerNoConsentMode.DIAL_ALONE,
  phonesPerLead: MAX_PHONES_PER_LEAD,
  nextPhoneTiming: DialerNextPhoneTiming.SAME_PASS,
  voicemailDropUrl: "",
  callbackMessage: "",
  callbackMessageUrl: "",
  enforceCallingHours: true,
  callingHoursStart: 8,
  callingHoursEnd: 21,
  rotateCallerIds: false,
  transcribeCalls: false,
};

const DETECTION_MODE_LABELS: Record<MachineDetectionMode, string> = {
  [MachineDetectionMode.CONNECT_FIRST]: "Connect first",
  [MachineDetectionMode.CONNECT_FIRST_WITH_DETECTION]: "Connect first and flag voicemails",
  [MachineDetectionMode.DETECT_FIRST]: "Detect voicemails first",
};

const DETECTION_MODE_HELP: Record<MachineDetectionMode, string> = {
  [MachineDetectionMode.CONNECT_FIRST]:
    "You hear the lead the moment they pick up. Voicemails reach you too, so hang up or leave your own message.",
  [MachineDetectionMode.CONNECT_FIRST_WITH_DETECTION]:
    "You hear the lead the moment they pick up, and a voicemail is flagged on screen so you can drop your message in one click. $0.01 per answered call.",
  [MachineDetectionMode.DETECT_FIRST]:
    "Voicemails get your drop message automatically, but people hear a few seconds of silence while detection runs, which can count as an abandoned call. $0.01 per answered call.",
};

const campaignSchema = z
  .object({
    name: z.string().min(1, "Campaign name is required"),
    description: z.string().optional(),
    type: z.nativeEnum(CampaignType),
    dialingMode: z.nativeEnum(DialingMode),
    phoneNumberId: z.string().optional(),
    holdRecordingUrl: z.string().optional(),
    callsPerAgent: z.coerce.number().int().min(1).max(10),
    retryAttempts: z.coerce.number().int().min(0).max(5),
    ringTimeout: z.coerce.number().int().min(10).max(120),
    wrapUpTime: z.coerce.number().int().min(0).max(300),
    workflowId: z.string().optional(),
    maxEnrollmentsPerDay: z.coerce.number().int().min(0).max(100000),
    stopOnReply: z.boolean(),
    scrubDnc: z.boolean(),
    retryDelayMinutes: z.coerce.number().int().min(1).max(1440),
    retryNoAnswerMinutes: z.number().int().min(1).nullable(),
    retryBusyMinutes: z.number().int().min(1).nullable(),
    retryVoicemailMinutes: z.number().int().min(1).nullable(),
    retryFailedMinutes: z.number().int().min(1).nullable(),
    maxAttemptsPerDay: z.number().int().min(1).nullable(),
    queueOrder: z.nativeEnum(DialerQueueOrder),
    skipDispositionIds: z.array(z.string()),
    defaultScriptId: z.string(),
    voicemailMessageFileId: z.string(),
    agentAccess: z.nativeEnum(CampaignAgentAccess),
    agentMembershipIds: z.array(z.number()),
    aiAgentId: z.string().optional(),
    machineDetectionMode: z.nativeEnum(MachineDetectionMode),
    noConsentMode: z.nativeEnum(DialerNoConsentMode),
    phonesPerLead: z.coerce.number().int().min(1).max(MAX_PHONES_PER_LEAD),
    nextPhoneTiming: z.nativeEnum(DialerNextPhoneTiming),
    voicemailDropUrl: z.string().optional(),
    callbackMessage: z.string().optional(),
    callbackMessageUrl: z.string().optional(),
    enforceCallingHours: z.boolean(),
    callingHoursStart: z.coerce.number().int().min(0).max(23),
    callingHoursEnd: z.coerce.number().int().min(1).max(24),
    rotateCallerIds: z.boolean(),
    transcribeCalls: z.boolean(),
    targetType: z.nativeEnum(CampaignTargetType).optional(),
    targetRef: z.string().optional(),
    audienceFilter: z.object({
      type: z.string(),
      dealStageIds: z.array(z.string()),
      assignedMembershipIds: z.array(z.number()),
      dataImportIds: z.array(z.string()),
      addedFrom: z.string(),
      addedTo: z.string(),
    }),
  })
  .refine((values) => values.type !== CampaignType.SEQUENCE || !!values.workflowId, {
    message: "Select an AI workflow for this campaign",
    path: ["workflowId"],
  })
  .refine((values) => values.targetType !== CampaignTargetType.CONTACT_LIST || !!values.targetRef, {
    message: "Select a contact list",
    path: ["targetRef"],
  })
  .refine(
    (values) =>
      (values.targetType !== CampaignTargetType.TAG && values.targetType !== CampaignTargetType.SAVED_SEARCH) ||
      !!values.targetRef,
    {
      message: "Choose who this campaign calls",
      path: ["targetRef"],
    },
  )
  .refine((values) => values.dialingMode === DialingMode.AI_AGENT || values.callsPerAgent <= MAX_LINES, {
    message: `The power dialer can dial up to ${MAX_LINES} lines`,
    path: ["callsPerAgent"],
  })
  .refine(
    (values) =>
      values.dialingMode === DialingMode.AI_AGENT ||
      values.type === CampaignType.SEQUENCE ||
      values.agentAccess !== CampaignAgentAccess.SELECTED ||
      values.agentMembershipIds.length > 0,
    {
      message: "Choose at least one agent",
      path: ["agentMembershipIds"],
    },
  )
  .refine(
    (values) =>
      values.dialingMode === DialingMode.AI_AGENT ||
      values.type === CampaignType.SEQUENCE ||
      values.ringTimeout >= MIN_RING_SECONDS,
    {
      message: `Let each call ring for at least ${MIN_RING_SECONDS} seconds`,
      path: ["ringTimeout"],
    },
  )
  .refine((values) => values.type === CampaignType.SEQUENCE || !!values.phoneNumberId, {
    message: "Choose a caller ID",
    path: ["phoneNumberId"],
  });

export type CampaignFormValues = z.infer<typeof campaignSchema>;

const AUDIENCE_FIELDS: (keyof CampaignFormValues)[] = ["targetType", "targetRef", "audienceFilter"];

function isFilterAudience(campaign?: Campaign): boolean {
  return campaign?.targetType === CampaignTargetType.FILTER;
}

function retryValuesOf(settings: CampaignSettings) {
  return {
    retryNoAnswerMinutes:
      settings.retryNoAnswerMinutes === undefined ? DEFAULT_RETRY_MINUTES : settings.retryNoAnswerMinutes,
    retryBusyMinutes: settings.retryBusyMinutes === undefined ? DEFAULT_RETRY_MINUTES : settings.retryBusyMinutes,
    retryVoicemailMinutes:
      settings.retryVoicemailMinutes === undefined ? DEFAULT_RETRY_MINUTES : settings.retryVoicemailMinutes,
    retryFailedMinutes: settings.retryFailedMinutes ?? null,
    maxAttemptsPerDay: settings.maxAttemptsPerDay ?? null,
    queueOrder: settings.queueOrder ?? DialerQueueOrder.STANDARD,
  };
}

export default function CampaignForm({ initialData, onSubmit, isSubmitting, submitLabel, leadsContent }: CampaignFormProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState(DETAILS_STEP);
  const hasSections = leadsContent !== undefined;
  const isStepped = hasSections && !initialData;
  const isTabbed = hasSections && !!initialData;
  const isLeadsStep = hasSections && step === LEADS_STEP;
  const [searchParams] = useSearchParams();
  const sourceSearchId = searchParams.get("searchId");
  const isSearchSourced = !!sourceSearchId || initialData?.targetType === CampaignTargetType.SAVED_SEARCH;
  const { data: phoneNumbers, isLoading: phonesLoading } = useGetOwnPhoneNumbersQuery();
  const { data: telephonySettings } = useGetTelephonySettingsQuery();
  const { data: workflowsPage } = useGetWorkflowsQuery({ size: 100 });

  const { data: aiAgents } = useGetAiAgentsQuery();
  const { data: quietHoursPolicy } = useGetQuietHoursPolicyQuery();
  const isCallingHoursOffForOrganisation = quietHoursPolicy?.policy === QuietHoursPolicy.OFF;
  const isCallTranscriptsOffForOrganisation = !telephonySettings?.callSummariesEnabled;

  // Build initial values from initialData if available so Radix <Select> components
  // receive the correct value on their very first render (avoids blank selects after reset).
  const s0 = initialData?.settings ?? DEFAULT_SETTINGS;

  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignSchema),
    shouldUnregister: false, // keep field values when conditionally unmounted (e.g. mode switch)
    defaultValues: {
      name: initialData?.name ?? "",
      description: initialData?.description ?? "",
      type: initialData?.type ?? CampaignType.POWER_DIALER,
      dialingMode: (initialData?.dialingMode ?? "POWER") as CampaignFormValues["dialingMode"],
      phoneNumberId: initialData?.phoneNumber?.id ?? "",
      holdRecordingUrl: initialData?.holdRecordingUrl ?? "",
      callsPerAgent: DEFAULT_SETTINGS.callsPerAgent,
      retryAttempts: DEFAULT_SETTINGS.retryAttempts,
      ringTimeout: DEFAULT_SETTINGS.ringTimeout,
      wrapUpTime: DEFAULT_SETTINGS.wrapUpTime,
      workflowId: "",
      maxEnrollmentsPerDay: 0,
      stopOnReply: true,
      scrubDnc: true,
      retryDelayMinutes: s0.retryDelayMinutes ?? DEFAULT_SETTINGS.retryDelayMinutes,
      ...retryValuesOf(s0),
      skipDispositionIds: s0.skipDispositionIds ?? [],
      defaultScriptId: s0.defaultScriptId ?? "",
      voicemailMessageFileId: s0.voicemailMessageFileId ?? "",
      agentAccess: initialData?.agentAccess ?? CampaignAgentAccess.EVERYONE,
      agentMembershipIds: initialData?.agents?.map((agent) => agent.membershipId) ?? [],
      aiAgentId: initialData?.aiAgent?.id ?? "",
      machineDetectionMode: s0.machineDetectionMode ?? MachineDetectionMode.CONNECT_FIRST,
      noConsentMode: s0.noConsentMode ?? DialerNoConsentMode.DIAL_ALONE,
      phonesPerLead: s0.phonesPerLead ?? MAX_PHONES_PER_LEAD,
      nextPhoneTiming: s0.nextPhoneTiming ?? DialerNextPhoneTiming.SAME_PASS,
      voicemailDropUrl: s0.voicemailDropUrl ?? "",
      callbackMessage: s0.callbackMessage ?? "",
      callbackMessageUrl: s0.callbackMessageUrl ?? "",
      enforceCallingHours: s0.enforceCallingHours ?? DEFAULT_SETTINGS.enforceCallingHours,
      callingHoursStart: s0.callingHoursStart ?? DEFAULT_SETTINGS.callingHoursStart,
      callingHoursEnd: s0.callingHoursEnd ?? DEFAULT_SETTINGS.callingHoursEnd,
      rotateCallerIds: s0.rotateCallerIds ?? DEFAULT_SETTINGS.rotateCallerIds,
      transcribeCalls: s0.transcribeCalls ?? DEFAULT_SETTINGS.transcribeCalls,
      targetType: initialData?.targetType ?? (sourceSearchId ? CampaignTargetType.SAVED_SEARCH : undefined),
      targetRef: isFilterAudience(initialData) ? "" : (initialData?.targetRef ?? sourceSearchId ?? ""),
      audienceFilter: isFilterAudience(initialData)
        ? parseAudienceFilter(initialData?.targetRef)
        : EMPTY_AUDIENCE_FILTER,
    },
  });

  const dialingMode = form.watch("dialingMode");
  const isAiMode = dialingMode === "AI_AGENT";
  const hasMultiLine = useAddOn(AddOnType.POWER_DIALER_MULTI);
  const maxLines = isAiMode ? MAX_AI_CALLS : hasMultiLine ? MAX_LINES : 1;

  const type = form.watch("type") as CampaignType;
  const isAgentDialed = !isAiMode && type !== CampaignType.SEQUENCE;
  const { data: members = [] } = useGetMembersQuery({ status: MembershipStatus.ACTIVE }, { skip: !isAgentDialed });
  const { data: dispositions = [] } = useGetCallDispositionsQuery({}, { skip: !isAgentDialed });
  const { data: callScripts = [] } = useGetCallScriptsQuery({}, { skip: !isAgentDialed });
  const workflows = workflowsPage?.items ?? [];
  const watchedTargetType = form.watch("targetType");
  const { data: contactListsPage } = useGetContactListsQuery(
    { size: 100 },
    { skip: type !== CampaignType.SEQUENCE },
  );
  const contactLists = contactListsPage?.items ?? [];

  const handleSubmit = async (values: CampaignFormValues) => {
    await onSubmit({
      name: values.name,
      description: values.description ?? "",
      type: values.type,
      dialingMode: values.dialingMode,
      phoneNumber: values.phoneNumberId ? { id: values.phoneNumberId } : undefined,
      holdRecordingUrl: !isAiMode ? (values.holdRecordingUrl || undefined) : undefined,
      aiAgent: isAiMode && values.aiAgentId ? { id: values.aiAgentId } : undefined,
      agentAccess: isAgentDialed ? values.agentAccess : CampaignAgentAccess.EVERYONE,
      agentMembershipIds:
        isAgentDialed && values.agentAccess === CampaignAgentAccess.SELECTED ? values.agentMembershipIds : [],
      settings: {
        callsPerAgent: values.callsPerAgent,
        retryAttempts: values.retryAttempts,
        retryDelayMinutes: values.retryDelayMinutes,
        retryNoAnswerMinutes: values.retryNoAnswerMinutes,
        retryBusyMinutes: values.retryBusyMinutes,
        retryVoicemailMinutes: values.retryVoicemailMinutes,
        retryFailedMinutes: values.retryFailedMinutes,
        maxAttemptsPerDay: values.maxAttemptsPerDay,
        queueOrder: values.queueOrder,
        skipDispositionIds: values.skipDispositionIds,
        defaultScriptId: isAgentDialed && values.defaultScriptId ? values.defaultScriptId : null,
        ringTimeout: values.ringTimeout,
        wrapUpTime: isAiMode ? 0 : values.wrapUpTime,
        machineDetectionMode: values.machineDetectionMode,
        noConsentMode: values.noConsentMode,
        phonesPerLead: values.phonesPerLead,
        nextPhoneTiming: values.nextPhoneTiming,
        voicemailDropUrl: values.voicemailDropUrl || null,
        voicemailMessageFileId: values.voicemailMessageFileId || null,
        callbackMessage: values.callbackMessage || null,
        callbackMessageUrl: values.callbackMessageUrl || null,
        enforceCallingHours: values.enforceCallingHours,
        callingHoursStart: values.callingHoursStart,
        callingHoursEnd: values.callingHoursEnd,
        rotateCallerIds: values.rotateCallerIds,
        transcribeCalls: values.transcribeCalls,
      },
      workflow: values.type === CampaignType.SEQUENCE && values.workflowId ? { id: values.workflowId } : undefined,
      targetType: values.targetType,
      targetRef:
        values.targetType === CampaignTargetType.FILTER
          ? serializeAudienceFilter(values.audienceFilter)
          : values.targetRef || undefined,
      maxEnrollmentsPerDay: values.maxEnrollmentsPerDay,
      stopOnReply: values.stopOnReply,
      scrubDnc: values.scrubDnc,
    });
  };

  const goToLeadsStep = async (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const detailFields = (Object.keys(form.getValues()) as (keyof CampaignFormValues)[]).filter(
      (fieldName) => !AUDIENCE_FIELDS.includes(fieldName),
    );
    const isValid = await form.trigger(detailFields, { shouldFocus: true });
    if (isValid) {
      setStep(LEADS_STEP);
      window.scrollTo({ top: 0 });
    }
  };

  const showSectionWithErrors = (errors: FieldErrors<CampaignFormValues>) => {
    if (!hasSections) {
      return;
    }
    const hasDetailErrors = (Object.keys(errors) as (keyof CampaignFormValues)[]).some(
      (fieldName) => !AUDIENCE_FIELDS.includes(fieldName),
    );
    setStep(hasDetailErrors ? DETAILS_STEP : LEADS_STEP);
  };

  useEffect(() => {
    if (initialData) {
      const settings = initialData.settings ?? DEFAULT_SETTINGS;
      form.reset({
        name: initialData.name ?? "",
        description: initialData.description ?? "",
        type: initialData.type ?? CampaignType.POWER_DIALER,
        dialingMode: initialData.dialingMode ?? DialingMode.POWER,
        phoneNumberId: initialData.phoneNumber?.id ?? "",
        holdRecordingUrl: initialData.holdRecordingUrl ?? "",
        callsPerAgent: settings.callsPerAgent,
        retryAttempts: settings.retryAttempts,
        ringTimeout: settings.ringTimeout,
        wrapUpTime: settings.wrapUpTime,
        workflowId: initialData.workflowId ?? "",
        maxEnrollmentsPerDay: initialData.maxEnrollmentsPerDay ?? 0,
        stopOnReply: initialData.stopOnReply ?? true,
        scrubDnc: initialData.scrubDnc ?? true,
        retryDelayMinutes: settings.retryDelayMinutes ?? DEFAULT_SETTINGS.retryDelayMinutes,
        ...retryValuesOf(settings),
        skipDispositionIds: settings.skipDispositionIds ?? [],
        defaultScriptId: settings.defaultScriptId ?? "",
        voicemailMessageFileId: settings.voicemailMessageFileId ?? "",
        agentAccess: initialData.agentAccess ?? CampaignAgentAccess.EVERYONE,
        agentMembershipIds: initialData.agents?.map((agent) => agent.membershipId) ?? [],
        aiAgentId: initialData.aiAgent?.id ?? "",
        machineDetectionMode: settings.machineDetectionMode ?? MachineDetectionMode.CONNECT_FIRST,
        noConsentMode: settings.noConsentMode ?? DialerNoConsentMode.DIAL_ALONE,
        phonesPerLead: settings.phonesPerLead ?? MAX_PHONES_PER_LEAD,
        nextPhoneTiming: settings.nextPhoneTiming ?? DialerNextPhoneTiming.SAME_PASS,
        voicemailDropUrl: settings.voicemailDropUrl ?? "",
        callbackMessage: settings.callbackMessage ?? "",
        callbackMessageUrl: settings.callbackMessageUrl ?? "",
        enforceCallingHours: settings.enforceCallingHours ?? DEFAULT_SETTINGS.enforceCallingHours,
        callingHoursStart: settings.callingHoursStart ?? DEFAULT_SETTINGS.callingHoursStart,
        callingHoursEnd: settings.callingHoursEnd ?? DEFAULT_SETTINGS.callingHoursEnd,
        rotateCallerIds: settings.rotateCallerIds ?? DEFAULT_SETTINGS.rotateCallerIds,
        transcribeCalls: settings.transcribeCalls ?? DEFAULT_SETTINGS.transcribeCalls,
        targetType: initialData.targetType ?? undefined,
        targetRef: isFilterAudience(initialData) ? "" : (initialData.targetRef ?? ""),
        audienceFilter: isFilterAudience(initialData)
          ? parseAudienceFilter(initialData.targetRef)
          : EMPTY_AUDIENCE_FILTER,
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (!phoneNumbers?.length || form.getValues("phoneNumberId")) {
      return;
    }
    const remembered = telephonySettings?.lastCallerIdPhoneNumberId;
    const isStillOwned = remembered && phoneNumbers.some((number) => number.id === remembered);
    const defaultCallerId = isStillOwned ? remembered : phoneNumbers[0].id;
    if (defaultCallerId) {
      form.setValue("phoneNumberId", defaultCallerId);
    }
  }, [phoneNumbers, telephonySettings, initialData]);

  return (
    <Form {...form}>
      <form
        onSubmit={isStepped && !isLeadsStep ? goToLeadsStep : form.handleSubmit(handleSubmit, showSectionWithErrors)}
        className="space-y-6"
      >
        {isStepped && <StepIndicator steps={CAMPAIGN_STEPS} currentStep={step} />}

        {isTabbed && (
          <Tabs value={String(step)} onValueChange={(value) => setStep(Number(value))}>
            <TabsList>
              {CAMPAIGN_STEPS.map((campaignStep, index) => (
                <TabsTrigger key={campaignStep.label} value={String(index + 1)}>
                  {campaignStep.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}

        <div className={cn("space-y-6", isLeadsStep && "hidden")}>
        {isSearchSourced && (
          <div className="rounded-md border bg-muted/40 px-4 py-3 text-sm">
            Sources leads from a saved property search. Existing imported leads are added now, and each new import batch
            auto-enrolls into this campaign.
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Campaign type</CardTitle>
            <CardDescription>Live dialing session or automated follow-up over time</CardDescription>
          </CardHeader>
          <CardContent>
            {/* TODO: revisit AI Drip Sequence. The option is hidden — not removed — until the
                sequence flow has been tested end to end; CampaignType.SEQUENCE and everything
                behind it still exists, and existing sequence campaigns are unaffected. Restore
                the second button and the grid-cols-2 wrapper below to bring it back. */}
            <div className="grid grid-cols-1 gap-4">
              <button
                type="button"
                onClick={() => form.setValue("type", CampaignType.POWER_DIALER)}
                className={cn(
                  "flex flex-col items-start gap-2 rounded-lg border-2 p-4 text-left transition-colors",
                  type === CampaignType.POWER_DIALER ? "border-primary bg-primary/5" : "border-muted hover:border-muted-foreground/30",
                )}
              >
                <Phone className="h-5 w-5" />
                <span className="font-medium">Power Dialer</span>
                <span className="text-xs text-muted-foreground">Dial the list live, right now: your team on the phones, or an AI agent making the calls for you.</span>
              </button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Name and describe your campaign</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField control={form.control} name="name" render={({ field }) => (
              <FormItem>
                <FormLabel>Campaign Name *</FormLabel>
                <FormControl><Input placeholder="e.g. Q1 Outreach" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl><Textarea placeholder="Optional description..." rows={3} {...field} /></FormControl>
              </FormItem>
            )} />
          </CardContent>
        </Card>

        {type === CampaignType.SEQUENCE ? (
          <>
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
              <span className="font-medium">Consent required.</span> The AI agent will only dial leads that have given
              call consent. Leads without consent (including freshly imported ones) are held as "awaiting consent" and
              never auto-dialed. They enroll automatically once consent is recorded (manual entry, web form, or opt-in).
            </div>

            <Card>
              <CardHeader>
                <CardTitle>AI Workflow</CardTitle>
                <CardDescription>The workflow the AI agent runs for each enrolled lead</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField control={form.control} name="workflowId" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Workflow *</FormLabel>
                    {workflows.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No workflows yet. Create one under Workflows first, then attach it here.
                      </p>
                    ) : (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Select a workflow" /></SelectTrigger></FormControl>
                        <SelectContent>
                          {workflows.map((wf) => (
                            <SelectItem key={wf.id} value={wf.id}>{wf.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <FormMessage />
                  </FormItem>
                )} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Audience & Pacing</CardTitle>
                <CardDescription>Control how fast leads are enrolled and when to stop</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField control={form.control} name="targetType" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Audience source</FormLabel>
                    <Select
                      value={field.value ?? "MANUAL"}
                      onValueChange={(value) => field.onChange(value === "MANUAL" ? undefined : value)}
                    >
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="MANUAL">Manually added contacts</SelectItem>
                        <SelectItem value={CampaignTargetType.CONTACT_LIST}>Contact list</SelectItem>
                        {isSearchSourced && (
                          <SelectItem value={CampaignTargetType.SAVED_SEARCH}>Saved property search</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />

                {watchedTargetType === CampaignTargetType.CONTACT_LIST && (
                  <FormField control={form.control} name="targetRef" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact list *</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Select a list" /></SelectTrigger></FormControl>
                        <SelectContent>
                          {contactLists.map((list) => (
                            <SelectItem key={list.id} value={list.id}>{list.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        List members enroll automatically. While the campaign is active, contacts added to the list
                        later join it too.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )} />
                )}

                <FormField control={form.control} name="maxEnrollmentsPerDay" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max enrollments per day</FormLabel>
                    <FormControl><Input type="number" min={0} max={100000} {...field} /></FormControl>
                    <p className="text-xs text-muted-foreground">0 means no daily limit. Drips new leads into the workflow over time.</p>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={form.control} name="stopOnReply" render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <Label>Stop on reply</Label>
                        <p className="text-xs text-muted-foreground">Cancel the cadence when a lead calls or texts back, so a human can take over.</p>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </div>
                  </FormItem>
                )} />

                <FormField control={form.control} name="scrubDnc" render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <Label>Scrub Do Not Call</Label>
                        <p className="text-xs text-muted-foreground">Skip leads whose number is flagged Do Not Call.</p>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </div>
                  </FormItem>
                )} />
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Dialing</CardTitle>
                <CardDescription>How the power dialer paces calls</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField control={form.control} name="dialingMode" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dialing Mode</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value={DialingMode.POWER}>Power</SelectItem>
                        <SelectItem value={DialingMode.PREVIEW}>Preview</SelectItem>
                        <SelectItem value={DialingMode.AI_AGENT}>AI Agent</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )} />
                {isAgentDialed && (
                  <FormField control={form.control} name="agentAccess" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Who Can Dial</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value={CampaignAgentAccess.EVERYONE}>Everyone with a dialer seat</SelectItem>
                          <SelectItem value={CampaignAgentAccess.SELECTED}>Selected agents</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Other agents don't see the campaign. Owners and admins can always open and monitor it.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )} />
                )}
                {isAgentDialed && form.watch("agentAccess") === CampaignAgentAccess.SELECTED && (
                  <FormField control={form.control} name="agentMembershipIds" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Agents</FormLabel>
                      <div className="space-y-2 rounded-md border border-border p-3">
                        {members.map((member) => {
                          const membershipId = Number(member.id);
                          const checkboxId = `campaign-agent-${member.id}`;
                          const agentLabel = memberName(member, member.user?.email || "Teammate");
                          return (
                            <div key={member.id} className="flex items-center gap-2">
                              <Checkbox
                                id={checkboxId}
                                checked={field.value.includes(membershipId)}
                                onCheckedChange={(checked) =>
                                  field.onChange(
                                    checked === true
                                      ? [...field.value, membershipId]
                                      : field.value.filter((selectedId) => selectedId !== membershipId),
                                  )
                                }
                              />
                              <Label htmlFor={checkboxId} className="text-sm font-normal">
                                {agentLabel}
                                {(member.dialerSeat ?? DialerSeat.NONE) === DialerSeat.NONE && (
                                  <span className="ml-1 text-muted-foreground">(no dialer seat)</span>
                                )}
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )} />
                )}
              </CardContent>
            </Card>
            {!hasSections && <CampaignAudienceCard form={form} />}
        </>
        )}

        {/* AI Agent selection - only for AI_AGENT mode */}
        {isAiMode && (
            <Card className="border-violet-200 bg-violet-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bot className="h-4 w-4 text-violet-500" />
                  AI Agent
                </CardTitle>
                <CardDescription>Select which AI agent will run this campaign</CardDescription>
              </CardHeader>
              <CardContent>
                <FormField control={form.control} name="aiAgentId" render={({ field }) => (
                    <FormItem>
                      <FormLabel>AI Agent *</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Select an AI agent" /></SelectTrigger></FormControl>
                        <SelectContent>
                          {(aiAgents ?? [])
                              .filter((a) => a.status === "ACTIVE")
                              .map((a) => (
                                  <SelectItem key={a.id} value={a.id!}>
                                    {a.name ?? a.id}
                                  </SelectItem>
                              ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                )} />
              </CardContent>
            </Card>
        )}

            <Card>
              <CardHeader>
                <CardTitle>Caller ID</CardTitle>
                <CardDescription>Select the phone number to use as the outbound caller ID</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField control={form.control} name="phoneNumberId" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    {phonesLoading ? (
                      <div className="text-sm text-muted-foreground">Loading phone numbers...</div>
                    ) : (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Select a phone number" /></SelectTrigger></FormControl>
                        <SelectContent>
                          {phoneNumbers?.map((pn) => (
                            <SelectItem key={pn.id} value={pn.id!!}>
                              {pn.alias ? `${pn.alias} (${pn.number})` : pn.number}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <FormMessage />
                  </FormItem>
                )} />

            {!isAiMode && (
              <FormField control={form.control} name="holdRecordingUrl" render={({ field }) => (
                <FormItem>
                  <FormLabel>Hold Music</FormLabel>
                  <AudioClipPicker
                    value={field.value}
                    onChange={(url) => field.onChange(url ?? "")}
                    placeholder="Default hold music"
                  />
                  <p className="text-xs text-muted-foreground">
                    Played while you have a lead on hold. Leave as None to use the built-in hold music.
                  </p>
                </FormItem>
              )} />
            )}
          </CardContent>
        </Card>

        {/* Dialer settings */}
        <Card>
          <CardHeader>
            <CardTitle>{isAiMode ? "Campaign Settings" : "Dialer Settings"}</CardTitle>
            <CardDescription>
              {isAiMode
                ? "Configure concurrency and retry behaviour for this AI campaign"
                : "Configure how the power dialer behaves"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="callsPerAgent" render={({ field }) => (
                <FormItem>
                  <FormLabel>{isAiMode ? "Concurrent Calls" : "Lines Per Agent"}</FormLabel>
                  <Select value={String(field.value)} onValueChange={(value) => field.onChange(Number(value))}>
                    <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      {Array.from({ length: maxLines }, (_, index) => index + 1).map((count) => (
                        <SelectItem key={count} value={String(count)}>
                          {String(count)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {isAiMode
                      ? `Max simultaneous AI calls at any time, up to ${MAX_AI_CALLS}`
                      : hasMultiLine
                        ? `How many lines each agent dials at once, up to ${MAX_LINES}`
                        : "One line per agent. Multi line dialing needs the multi line add-on."}
                  </p>
                  <FormMessage />
                </FormItem>
              )} />

                  <FormField control={form.control} name="ringTimeout" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ring Timeout (seconds)</FormLabel>
                      <FormControl>
                        <Input type="number" min={isAiMode ? 10 : MIN_RING_SECONDS} max={120} {...field} />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">How long to ring before marking as no answer</p>
                      <FormMessage />
                    </FormItem>
                  )} />

              <FormField control={form.control} name="retryAttempts" render={({ field }) => (
                <FormItem>
                  <FormLabel>Retry Attempts</FormLabel>
                  <Select value={String(field.value)} onValueChange={(value) => field.onChange(Number(value))}>
                    <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      {Array.from({ length: MAX_RETRY_ATTEMPTS + 1 }, (_, index) => index).map((count) => (
                        <SelectItem key={count} value={String(count)}>
                          {count === 0 ? "No retries" : String(count)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {isAiMode ? "Retries for unanswered / failed contacts" : "Most times a lead is called again, in total"}
                  </p>
                  <FormMessage />
                </FormItem>
              )} />

              {isAiMode ? (
                <FormField control={form.control} name="retryDelayMinutes" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Retry Delay (minutes)</FormLabel>
                    <FormControl><Input type="number" min={1} max={1440} {...field} /></FormControl>
                    <p className="text-xs text-muted-foreground">Time to wait before retrying a contact</p>
                    <FormMessage />
                  </FormItem>
                )} />
              ) : (
                <FormField control={form.control} name="wrapUpTime" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Wrap-Up Time (seconds)</FormLabel>
                    <FormControl><Input type="number" min={0} max={300} {...field} /></FormControl>
                    <p className="text-xs text-muted-foreground">
                      After each call, dialing waits for Save and next. Set a time to save and move on automatically, or 0 to always wait.
                    </p>
                    <FormMessage />
                  </FormItem>
                )} />
              )}
            </div>

            {!isAiMode && (
              <div className="grid grid-cols-2 gap-4">
                <FormField control={form.control} name="phonesPerLead" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Numbers To Try Per Lead</FormLabel>
                    <Select value={String(field.value)} onValueChange={(value) => field.onChange(Number(value))}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {Array.from({ length: MAX_PHONES_PER_LEAD }, (_, index) => index + 1).map((count) => (
                          <SelectItem key={count} value={String(count)}>
                            {count === MAX_PHONES_PER_LEAD ? "All" : String(count)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Phones are tried in skip trace rank, then list order. Do not call, wrong and disconnected
                      numbers are skipped.
                    </p>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="nextPhoneTiming" render={({ field }) => (
                  <FormItem>
                    <FormLabel>After No Answer Or Busy</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value={DialerNextPhoneTiming.SAME_PASS}>Try the next number right away</SelectItem>
                        <SelectItem value={DialerNextPhoneTiming.NEXT_PASS}>Try the next number on the next pass</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            )}

            {isAgentDialed && (
              <FormField control={form.control} name="defaultScriptId" render={({ field }) => (
                <FormItem>
                  <FormLabel>Script</FormLabel>
                  <Select
                    value={field.value || NO_SCRIPT}
                    onValueChange={(value) => field.onChange(value === NO_SCRIPT ? "" : value)}
                  >
                    <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      <SelectItem value={NO_SCRIPT}>No script</SelectItem>
                      {callScripts.map((script) => (
                        <SelectItem key={script.id} value={script.id}>{script.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Shown on the call screen with the lead's details filled in. Manage scripts in Telephony settings.
                  </p>
                  <FormMessage />
                </FormItem>
              )} />
            )}

            {!isAiMode && (
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium">Retry After</p>
                  <p className="text-xs text-muted-foreground">
                    {form.watch("retryAttempts") > 0
                      ? "How long to wait before calling a lead again, by how the last call ended."
                      : "Choose Retry Attempts above to call leads again."}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {RETRY_OUTCOME_FIELDS.map(({ name, label }) => (
                    <FormField key={name} control={form.control} name={name} render={({ field }) => (
                      <FormItem>
                        <FormLabel>{label}</FormLabel>
                        <Select
                          value={field.value === null ? NO_RETRY : String(field.value)}
                          onValueChange={(value) => field.onChange(value === NO_RETRY ? null : Number(value))}
                          disabled={form.watch("retryAttempts") === 0}
                        >
                          <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                          <SelectContent>
                            <SelectItem value={NO_RETRY}>No retry</SelectItem>
                            {retryDelayChoices(field.value).map((minutes) => (
                              <SelectItem key={minutes} value={String(minutes)}>
                                {formatRetryDelay(minutes)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="maxAttemptsPerDay" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Calls Per Lead Per Day</FormLabel>
                      <Select
                        value={field.value === null ? NO_DAILY_LIMIT : String(field.value)}
                        onValueChange={(value) => field.onChange(value === NO_DAILY_LIMIT ? null : Number(value))}
                      >
                        <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value={NO_DAILY_LIMIT}>No daily limit</SelectItem>
                          {Array.from({ length: MAX_DAILY_ATTEMPTS }, (_, index) => index + 1).map((count) => (
                            <SelectItem key={count} value={String(count)}>
                              {String(count)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        A retry past this limit waits for the lead's next day. State call limits always apply.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="queueOrder" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Queue Order</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value={DialerQueueOrder.STANDARD}>Callbacks, retries, then new leads</SelectItem>
                          <SelectItem value={DialerQueueOrder.CLOSING_WINDOW_FIRST}>
                            Calling hours ending soonest first
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">{QUEUE_ORDER_HELP[field.value]}</p>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                {dispositions.length > 0 && (
                  <FormField control={form.control} name="skipDispositionIds" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Skip Leads Whose Last Disposition Is</FormLabel>
                      <div className="grid grid-cols-2 gap-2 rounded-md border border-border p-3">
                        {dispositions.map((disposition) => {
                          const checkboxId = `skip-disposition-${disposition.id}`;
                          return (
                            <div key={disposition.id} className="flex items-center gap-2">
                              <Checkbox
                                id={checkboxId}
                                checked={field.value.includes(disposition.id)}
                                onCheckedChange={(checked) =>
                                  field.onChange(
                                    checked === true
                                      ? [...field.value, disposition.id]
                                      : field.value.filter((selectedId) => selectedId !== disposition.id),
                                  )
                                }
                              />
                              <Label htmlFor={checkboxId} className="text-sm font-normal">
                                {disposition.name}
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Checked when a lead comes up to be dialed, using the last disposition from any call.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )} />
                )}
              </div>
            )}

            {!isAiMode && form.watch("callsPerAgent") > 1 && (
              <FormField control={form.control} name="noConsentMode" render={({ field }) => (
                <FormItem>
                  <FormLabel>Numbers Without Written Consent</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      <SelectItem value={DialerNoConsentMode.DIAL_ALONE}>Dial them one at a time</SelectItem>
                      <SelectItem value={DialerNoConsentMode.SKIP}>Skip them</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Extra lines only carry numbers with written call consent. Others wait for the open lines to finish
                    and ring on their own, or are skipped.
                  </p>
                  <FormMessage />
                </FormItem>
              )} />
            )}
          </CardContent>
        </Card>

        {!isAiMode && type !== CampaignType.SEQUENCE && (
          <Card>
            <CardHeader>
              <CardTitle>Compliance & Quality</CardTitle>
              <CardDescription>Voicemail detection, calling hours, and lead protection</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField control={form.control} name="machineDetectionMode" render={({ field }) => (
                <FormItem>
                  <FormLabel>Voicemail Detection</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      {Object.values(MachineDetectionMode).map((mode) => (
                        <SelectItem key={mode} value={mode}>
                          {DETECTION_MODE_LABELS[mode]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{DETECTION_MODE_HELP[field.value]}</p>
                </FormItem>
              )} />

              <FormField control={form.control} name="voicemailMessageFileId" render={({ field }) => (
                <FormItem>
                  <FormLabel>Voicemail Message</FormLabel>
                  <AudioClipPicker
                    value={field.value}
                    onChange={(fileId) => field.onChange(fileId ?? "")}
                    placeholder={form.watch("voicemailDropUrl") ? "Current custom recording" : "None, just hang up"}
                    category={AudioLibrary.VOICEMAIL_MESSAGE}
                    valueKey="id"
                  />
                  <p className="text-xs text-muted-foreground">
                    {form.watch("machineDetectionMode") === MachineDetectionMode.DETECT_FIRST
                      ? "Left automatically when a voicemail answers, or when an agent presses Drop voicemail."
                      : "Left when an agent presses Drop voicemail on the live call."}{" "}
                    Only numbers with written call consent in states that allow recorded messages get it.
                  </p>
                </FormItem>
              )} />

              <FormField control={form.control} name="callbackMessage" render={({ field }) => (
                <FormItem>
                  <FormLabel>Callback message</FormLabel>
                  <FormControl><Textarea rows={3} placeholder="Leave empty to use the platform default" {...field} /></FormControl>
                  <p className="text-xs text-muted-foreground">
                    Played to a lead who answers while you are busy, only on numbers with written call consent. It has to say the call was for telemarketing purposes, name the business with {"{business}"}, give the callback number with {"{number}"} and explain the opt-out key with {"{digit}"}.
                  </p>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField control={form.control} name="callbackMessageUrl" render={({ field }) => (
                <FormItem>
                  <FormLabel>Callback recording</FormLabel>
                  <AudioClipPicker
                    value={field.value}
                    onChange={(url) => field.onChange(url ?? "")}
                    placeholder="Read the message aloud"
                    category={AudioLibrary.VOICEMAIL_MESSAGE}
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional recording played instead of the spoken text. It has to say the same things; the text above is what gets checked.
                  </p>
                </FormItem>
              )} />

              <FormField control={form.control} name="enforceCallingHours" render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label>Respect Calling Hours</Label>
                      <p className="text-xs text-muted-foreground">
                        {isCallingHoursOffForOrganisation
                          ? "Calling hours are off for your organisation. Turn them on in Settings, Telephony."
                          : "Only dial leads inside their state's calling hours, in their local time. The hours below can narrow them further."}
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={!isCallingHoursOffForOrganisation && field.value}
                        onCheckedChange={field.onChange}
                        disabled={isCallingHoursOffForOrganisation}
                      />
                    </FormControl>
                  </div>
                </FormItem>
              )} />

              {!isCallingHoursOffForOrganisation && form.watch("enforceCallingHours") && (
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="callingHoursStart" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Earliest Hour (local)</FormLabel>
                      <FormControl><Input type="number" min={0} max={23} {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="callingHoursEnd" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Latest Hour (local)</FormLabel>
                      <FormControl><Input type="number" min={1} max={24} {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
              )}

              <FormField control={form.control} name="rotateCallerIds" render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label>Rotate Caller IDs</Label>
                      <p className="text-xs text-muted-foreground">
                        Spread calls across all your numbers, preferring a caller ID matching the lead's area code
                      </p>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </div>
                </FormItem>
              )} />

              <FormField control={form.control} name="transcribeCalls" render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label>Transcribe and Summarize Calls</Label>
                      <p className="text-xs text-muted-foreground">
                        {isCallTranscriptsOffForOrganisation
                          ? "Call transcripts are off for your organisation. Turn them on in Settings, Telephony."
                          : `Each recorded conversation is written out and summarized with key points, a next step and a suggested follow-up task. $${telephonySettings?.transcriptionRate ?? 0} per transcribed minute.`}
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={!isCallTranscriptsOffForOrganisation && field.value}
                        onCheckedChange={field.onChange}
                        disabled={isCallTranscriptsOffForOrganisation}
                      />
                    </FormControl>
                  </div>
                </FormItem>
              )} />
            </CardContent>
          </Card>
        )}
        </div>

        {isLeadsStep && (
          <>
            <CampaignAudienceCard form={form} />
            {(isTabbed || !watchedTargetType) && leadsContent}
          </>
        )}

        <div className="flex items-center justify-between">
          {isStepped && isLeadsStep ? (
            <Button type="button" variant="ghost" onClick={() => setStep(DETAILS_STEP)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={() => navigate("/campaigns")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Campaigns
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isStepped && !isLeadsStep ? "Continue" : submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
